/**
 * Firebase Initialization — MALYALI Personel Sistemi
 *
 * Round 1 NOT: Firebase konfigürasyonu .env'den okunur.
 *              .env yoksa veya placeholder ise initialization yapılmaz,
 *              sistem yine de çalışır (mock mode).
 *
 * Round 2'de gerçek Firebase project bilgileri girilecek.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
  connectFirestoreEmulator,
} from 'firebase/firestore'
import { getStorage, type FirebaseStorage, connectStorageEmulator } from 'firebase/storage'
import { getFunctions, type Functions, connectFunctionsEmulator } from 'firebase/functions'

// ═══════════════════════════════════════════════════════════════════
// KONFIGÜRASYON — .env'den okunur
// ═══════════════════════════════════════════════════════════════════

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

const useEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true'

// ═══════════════════════════════════════════════════════════════════
// LAZY INITIALIZATION
// ═══════════════════════════════════════════════════════════════════

let app: FirebaseApp | null = null
let _auth: Auth | null = null
let _db: Firestore | null = null
let _storage: FirebaseStorage | null = null
let _functions: Functions | null = null

/**
 * Firebase'in konfigüre edilip edilmediğini kontrol eder.
 * Round 1'de .env yoksa false döner, sistem mock modda çalışır.
 */
export function isFirebaseConfigured(): boolean {
  return (
    !!firebaseConfig.apiKey &&
    firebaseConfig.apiKey !== 'your-api-key-here' &&
    !!firebaseConfig.projectId
  )
}

/**
 * Firebase app'i tek seferlik initialize eder.
 */
function ensureInitialized(): FirebaseApp {
  if (app) return app

  if (!isFirebaseConfigured()) {
    throw new Error(
      'Firebase henüz konfigüre edilmedi. .env dosyasını kontrol et veya Round 2 setup\'ını tamamla.'
    )
  }

  app = initializeApp(firebaseConfig)
  return app
}

// ═══════════════════════════════════════════════════════════════════
// SERVİS GETTER'LARI — Lazy + cached
// ═══════════════════════════════════════════════════════════════════

export function getFirebaseAuth(): Auth {
  if (_auth) return _auth
  const application = ensureInitialized()
  _auth = getAuth(application)
  return _auth
}

export function getFirestoreDb(): Firestore {
  if (_db) return _db
  const application = ensureInitialized()

  // Emulator modunda persistent cache kullanma — getFirestore yeterli
  // Production: IndexedDB cache + çoklu tab desteği (offline yazma kuyruğu)
  if (useEmulator) {
    _db = getFirestore(application)
    connectFirestoreEmulator(_db, 'localhost', 8080)
  } else {
    try {
      _db = initializeFirestore(application, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      })
    } catch (err) {
      // Tarayıcı IndexedDB desteklemiyorsa veya başka tab açıksa fallback
      console.warn('[firebase] persistent cache kurulamadı, memory cache kullanılıyor:', err)
      _db = getFirestore(application)
    }
  }

  return _db
}

export function getFirebaseStorage(): FirebaseStorage {
  if (_storage) return _storage
  const application = ensureInitialized()
  _storage = getStorage(application)

  if (useEmulator) {
    connectStorageEmulator(_storage, 'localhost', 9199)
  }

  return _storage
}

export function getFirebaseFunctions(): Functions {
  if (_functions) return _functions
  const application = ensureInitialized()
  // Cloud Functions europe-west3'te (KARAR #1)
  _functions = getFunctions(application, 'europe-west3')

  if (useEmulator) {
    connectFunctionsEmulator(_functions, 'localhost', 5001)
  }

  return _functions
}
